import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAccessToken, useDistributionPrincipal } from '@dss/auth'
import { ApiError, groupInvitationLink, issueGroupInvitation, listGroupInvitations,
  revokeGroupInvitation, beginGroupInvitationAttempt, finishGroupInvitationAttempt,
  GroupInvitationAttemptError, type IssuedGroupInvitation, type PortalAccess } from '@dss/api-client'
import { Banner, Button, Card, Input } from '@dss/design-system'
import { api } from '../../lib/api'
import { Link } from '../../lib/router'
import { useClientGroupAccess, usePortalSession } from './usePortalMembership'
import { ClientEmployeeLifecycle } from '../client-employees/ClientEmployeeLifecycle'
import './invitations.css'
import { ClientShell } from '../../components/ClientShell'

export function ClientInvitationWorkspace() {
  const principal = useDistributionPrincipal()
  return <ClientShell active="employees" commerceContext={false}>
    <ClientEmployeeWorkspace key={`${principal?.distributorId}:${principal?.userId}`} />
  </ClientShell>
}

function ClientEmployeeWorkspace() {
  const principal = useDistributionPrincipal()
  const access = useClientGroupAccess()
  const session = usePortalSession()
  const [accessLost, setAccessLost] = useState(false)
  const hideAccess = useCallback(() => setAccessLost(true), [])
  const name = session.data && 'clientGroupName' in session.data
    && typeof session.data.clientGroupName === 'string' ? session.data.clientGroupName : 'Моя группа'
  if (access.isPending || session.isPending) return <p role="status">Проверяем группу и назначения…</p>
  if (accessLost || access.isError || session.isError || access.data.membership.role !== 'CLIENT_OWNER'
    || !access.data.permissions.some(code => ['invitation.create', 'client.users.manage'].includes(code))) {
    return <div className="group-invitation-page"><Banner variant="danger"
      body="Управлять сотрудниками может владелец своей группы с действующим разрешением. Данные скрыты." />
      <Link to="/me">Вернуться в портал</Link></div>
  }
  return <div className="group-invitation-page">
    <h1>Сотрудники и приглашения</h1><p>Группа «{name}»</p>
    {access.data.permissions.includes('client.users.manage') && <ClientEmployeeLifecycle
      key={`${principal?.distributorId}:${principal?.userId}:${access.data.membership.clientGroupId}`}
      groupId={access.data.membership.clientGroupId!} onAccessLost={hideAccess} />}
    {access.data.permissions.includes('invitation.create') && <EmployeeInvitationForm
      key={`${principal?.distributorId}:${principal?.userId}`} access={access.data} onAccessLost={hideAccess} />}
  </div>
}

function EmployeeInvitationForm({ access, onAccessLost }: { access: PortalAccess; onAccessLost: () => void }) {
  const token = useAccessToken()
  const principal = useDistributionPrincipal()
  const groupId = access.membership.clientGroupId!
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<string[]>([])
  const [canOrder, setCanOrder] = useState(true)
  const [pending, setPending] = useState(false)
  const [issued, setIssued] = useState<IssuedGroupInvitation | null>(null)
  const [failure, setFailure] = useState<unknown>(null)
  const [copied, setCopied] = useState(false)
  const attempt = useRef<{ fingerprint: string; id: string } | null>(null)
  const attemptScope = `${principal?.distributorId}:${principal?.userId}:${groupId}:CLIENT_EMPLOYEE`
  const list = useQuery({ queryKey: ['group-invites', principal?.distributorId,
    principal?.userId, groupId, 'CLIENT_EMPLOYEE'],
    queryFn: () => listGroupInvitations(api, token!, groupId, 'CLIENT_EMPLOYEE'),
    enabled: Boolean(token && access.permissions.includes('invitation.list')), retry: false })
  const pointMap = new Map(access.tradepoints.map((point) => [point.id, point]))
  const payerMap = new Map(access.payers.map((payer) => [payer.id, payer]))
  const choices = access.pairs.map((pair) => {
    const point = pointMap.get(pair.tradepointId)
    const payer = payerMap.get(pair.payerId)
    return { ...pair, key: `${pair.tradepointId}:${pair.payerId}`,
      label: `${point?.name ?? 'Точка'} · №${pair.tradepointId} · ${point?.address ?? 'Адрес не указан'} — ${payer?.name ?? 'Плательщик'} · №${pair.payerId} · ИНН ${payer?.inn ?? 'не указан'}` }
  })
  const visible = choices.filter((pair) => pair.label.toLocaleLowerCase('ru-RU')
    .includes(search.trim().toLocaleLowerCase('ru-RU')))
  const staleSelection = selected.some((key) => !choices.some((pair) => pair.key === key))
  const denied = [failure, list.error].some((error) => error instanceof ApiError
    && [401, 403].includes(error.status))
  useEffect(() => { if (denied) onAccessLost() }, [denied, onAccessLost])
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!token || pending || !selected.length || staleSelection) return
    const chosen = choices.filter((pair) => selected.includes(pair.key))
      .map(({ tradepointId, payerId }) => ({ tradepointId, payerId }))
    const fingerprint = JSON.stringify([groupId, email.trim().toLowerCase(), name.trim(), canOrder,
      [...selected].sort()])
    setPending(true); setFailure(null); setCopied(false)
    try {
      if (attempt.current?.fingerprint !== fingerprint) attempt.current = { fingerprint,
        id: await beginGroupInvitationAttempt(attemptScope, fingerprint) }
      setIssued(await issueGroupInvitation(api, token, { requestId: attempt.current.id, groupId,
        role: 'CLIENT_EMPLOYEE', email: email.trim().toLowerCase(), name: name.trim(),
        pairs: chosen, canCreateOrders: canOrder, confirmsOwnerAccess: false }))
      finishGroupInvitationAttempt(attemptScope)
      if (access.permissions.includes('invitation.list')) void list.refetch()
    } catch (error) { setFailure(error) } finally { setPending(false) }
  }
  async function revoke(id: string) {
    if (!token || pending) return
    setPending(true); setFailure(null)
    try {
      await revokeGroupInvitation(api, token, id)
      if (issued?.invitation.id === id) setIssued(null)
      void list.refetch()
    } catch (error) { setFailure(error) } finally { setPending(false) }
  }
  if (denied) return <Banner variant="danger" body="Право управления приглашениями изменилось. Обновите страницу и обратитесь к владельцу." />
  return <Card padding="lg">
    <p>Сотрудник получит только отмеченные пары точки и плательщика. Новые точки группы автоматически ему не назначаются.</p>
    {Boolean(failure) && <Banner variant="danger" body={failure instanceof GroupInvitationAttemptError
      ? failure.message : 'Приглашение не подтверждено. Состав или права могли измениться. Повтор того же запроса проверит прежний результат; не создавайте второй запрос до проверки.'} />}
    {staleSelection && <Banner variant="warning" body="Часть выбранных пар стала недоступна. Сбросьте выбор и выберите текущие назначения." />}
    <form className="invitation-form" onSubmit={(event) => void submit(event)}>
      <fieldset disabled={pending} style={{ border: 0, padding: 0, minWidth: 0 }}>
        <legend>Новый сотрудник клиента</legend>
        <label htmlFor="employee-name">Имя<Input id="employee-name" required autoComplete="name" maxLength={200} value={name}
          onChange={(event) => { setName(event.target.value); setIssued(null) }} /></label>
        <label htmlFor="employee-email">Email<Input id="employee-email" required type="email" autoComplete="email" maxLength={254} value={email}
          onChange={(event) => { setEmail(event.target.value); setIssued(null) }} /></label>
        <label htmlFor="employee-pair-search">Поиск точки, адреса или плательщика<Input id="employee-pair-search" value={search}
          onChange={(event) => setSearch(event.target.value)} /></label>
        <p role="status">Выбрано пар: {selected.length}</p>
        <Button onClick={() => { setSelected([]); setIssued(null) }}>Сбросить выбор</Button>
        <ul className="invitation-pairs">{visible.map((pair) => <li key={pair.key}>
          <label className="invitation-check"><input type="checkbox" checked={selected.includes(pair.key)}
            onChange={(event) => { setIssued(null); setSelected(event.target.checked
              ? [...selected, pair.key] : selected.filter((key) => key !== pair.key)) }} />{pair.label}</label>
        </li>)}</ul>
        {!visible.length && <p>Подходящих разрешённых пар нет.</p>}
        <label className="invitation-check"><input type="checkbox" checked={canOrder}
          onChange={(event) => { setCanOrder(event.target.checked); setIssued(null) }} /> Разрешить корзину и создание заказов</label>
        <p>Без этого разрешения сотрудник только просматривает доступные данные. Редактирование цен и отмена заказов не выдаются.</p>
        <Button type="submit" variant="primary" loading={pending}
          disabled={!selected.length || staleSelection}>Создать приглашение сотрудника</Button>
      </fieldset>
    </form>
    {issued && issued.invitation.state === 'PENDING' && <section aria-label="Созданное приглашение">
      <p>Передайте ссылку только {issued.invitation.email}. До {new Date(issued.invitation.expiresAt).toLocaleString('ru-RU')}.</p>
      <label htmlFor="employee-secret">Секретная ссылка<Input id="employee-secret" readOnly value={groupInvitationLink(window.location.origin, issued.token)} /></label>
      <Button onClick={() => {
        if (!navigator.clipboard) return
        void navigator.clipboard.writeText(groupInvitationLink(window.location.origin, issued.token))
          .then(() => setCopied(true)).catch(() => setCopied(false))
      }}>Копировать ссылку</Button>
      <p role="status">{copied ? 'Скопировано' : 'Или выделите и скопируйте поле вручную.'}</p>
      <Button onClick={() => setIssued(null)}>Скрыть ссылку</Button>
    </section>}
    {access.permissions.includes('invitation.list') && <section><h2>Последние приглашения</h2>
      <Button disabled={pending} onClick={() => void list.refetch()}>Обновить приглашения</Button>
      <Button disabled={pending || list.isError || list.isPending} onClick={() => {
        if (!window.confirm('Вы проверили список и завершили предыдущую попытку? Найденное приглашение можно восстановить повтором прежних данных или отозвать; не создавайте дубликат.')) return
        try { finishGroupInvitationAttempt(attemptScope); attempt.current = null; setFailure(null) }
        catch (error) { setFailure(error) }
      }}>Предыдущая попытка проверена — разрешить новый запрос</Button>
      {list.isPending ? <p>Загружаем…</p> : list.isError ? <p role="alert">Список недоступен.</p>
        : <ul>{list.data.map((item) => <li key={item.id}>
          {item.name} · {item.email} · {({ PENDING: 'Ожидает принятия', ACCEPTED: 'Принято',
            REVOKED: 'Отозвано', EXPIRED: 'Истекло' })[item.state]}
          {item.state === 'PENDING' && access.permissions.includes('invitation.revoke')
            && <Button disabled={pending} onClick={() => void revoke(item.id)}>Отозвать {item.name}</Button>}
        </li>)}</ul>}
    </section>}
  </Card>
}
